import { createApp, ref, computed, onMounted } from 'https://unpkg.com/vue@3.4.33/dist/vue.esm-browser.prod.js';

createApp({
  setup() {
    const discountCode = ref('');
    const appliedDiscounts = ref([]);
    const storefrontAccessToken = window.cartDiscount.storefrontAccessToken || null;
    const isProcessing = ref(false);
    const errorMessage = ref('');

    onMounted(() => {
      window.refreshCartData();
    });

    // Add event listener for cart updates
    $(document).on('cart.updated', function (event, cart) {
      const discountCodes = extractDiscountCodes(cart);
      appliedDiscounts.value = discountCodes;
    });

    const refreshCartData = async () => {
      window.refreshCartData();
      const response = await fetch('/cart?section_id=cart-template');

      if (!response.ok) {
        console.error('Failed to fetch cart section');
        return;
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selectors = ['table tbody', '#cart-subtotals'];
      const cartElement = document.querySelector('#shopify-section-cart-template');

      if (!cartElement) {
        return;
      }

      for (const selector of selectors) {
        const targetElement = cartElement.querySelector(selector);
        const sourceElement = html.querySelector(selector);

        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }

      window.ogWidget.init();
    };

    const cartId = computed(() => {
      if (document.cookie.includes('cart=')) {
        const cartCookie = decodeURIComponent(document.cookie.split('cart=')[1].split(';')[0]);
        return `gid://shopify/Cart/${cartCookie}`
      }

      return ''
    });

    // Utility function to extract discount codes from cart object
    const extractDiscountCodes = (cart) => {
      const discountCodes = [];

      // Skip if cart is empty
      if (!cart || !cart.items || cart.items.length === 0) {
        return discountCodes;
      }

      // Check for line item discounts (Liquid format)
      cart.items.forEach(item => {
        if (item.discounts) {
          item.discounts.forEach(discount => {
            if (discount.title && !discountCodes.includes(discount.title)) {
              discountCodes.push(discount.title);
            }
          });
        }

        // Also check for line_level_discount_allocations (cart.js format)
        if (item.line_level_discount_allocations) {
          item.line_level_discount_allocations.forEach(discount => {
            if (discount.discount_application &&
              discount.discount_application.title &&
              !discountCodes.includes(discount.discount_application.title)) {
              discountCodes.push(discount.discount_application.title);
            }
          });
        }
      });

      // Check for cart-level discounts
      if (cart.cart_level_discount_applications) {
        cart.cart_level_discount_applications.forEach(discount => {
          if (discount.title && !discountCodes.includes(discount.title)) {
            discountCodes.push(discount.title);
          }
        });
      }

      return discountCodes;
    };

    const updateDiscountCodes = async (discountCodes, isRemoval = false, removedCode = '') => {
      isProcessing.value = true;
      errorMessage.value = '';

      try {
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontAccessToken
          },
          body: JSON.stringify({
            query: `
              mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) {
                cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                  cart {
                    id
                    discountCodes {
                      code
                      applicable
                    }
                  }
                  userErrors {
                    field
                    message
                  }
                }
              }
            `,
            variables: {
              cartId: cartId.value,
              discountCodes: discountCodes
            }
          })
        });

        const data = await response.json();

        // Check for GraphQL errors
        if (data.errors) {
          const errorMsg = data.errors[0].message || `An error occurred while ${isRemoval ? 'removing' : 'applying'} the discount`;
          return { success: false, error: errorMsg };
        }

        const { userErrors, cart } = data.data.cartDiscountCodesUpdate;

        // Check for user errors
        if (userErrors && userErrors.length > 0) {
          const errorMsg = userErrors[0].message || `${isRemoval ? 'An error occurred while removing' : 'Invalid'} discount code`;
          return { success: false, error: errorMsg };
        }

        // Get updated discount codes from response
        if (cart && cart.discountCodes) {
          const updatedDiscounts = cart.discountCodes
            .filter(discount => discount.applicable)
            .map(discount => discount.code);

          return { success: true, updatedDiscounts };
        } else {
          // Fallback in case cart data is missing
          return { success: true, updatedDiscounts: discountCodes };
        }
      } catch (error) {
        return {
          success: false,
          error: error.message || `An error occurred while ${isRemoval ? 'removing' : 'applying'} the discount`
        };
      }
    };

    const applyDiscount = async () => {
      if (!discountCode.value) return;

      // Check if discount code is already applied (case-insensitive)
      const upperCaseDiscountCodes = appliedDiscounts.value.map(code => code.toUpperCase());
      if (upperCaseDiscountCodes.includes(discountCode.value.toUpperCase())) {
        errorMessage.value = 'This discount code is already applied';
        return;
      }

      isProcessing.value = true;
      errorMessage.value = '';

      const newDiscountsCodes = [...appliedDiscounts.value, discountCode.value];
      const result = await updateDiscountCodes(newDiscountsCodes);

      if (result.success) {
        appliedDiscounts.value = result.updatedDiscounts;

        const updatedUpperCaseDiscountCodes = appliedDiscounts.value.map(code => code.toUpperCase());
        if (!updatedUpperCaseDiscountCodes.includes(discountCode.value.toUpperCase())) {
          errorMessage.value = 'Invalid discount code';
        } else {
          discountCode.value = ''; // Clear the input field
        }

        // Refresh cart data to update pricing
        refreshCartData();
      } else {
        errorMessage.value = result.error;
      }

      isProcessing.value = false;
    };

    const removeDiscount = async (code) => {
      isProcessing.value = true;
      errorMessage.value = '';

      const newDiscounts = appliedDiscounts.value.filter(c => c !== code);
      const result = await updateDiscountCodes(newDiscounts, true, code);

      if (result.success) {
        appliedDiscounts.value = result.updatedDiscounts;
        // Refresh cart data to update pricing
        refreshCartData();
      } else {
        errorMessage.value = result.error;
      }

      isProcessing.value = false;
    };

    return {
      discountCode,
      appliedDiscounts,
      isProcessing,
      errorMessage,
      applyDiscount,
      removeDiscount
    };
  }
}).mount('#cart-discount');